import { BaseClient } from '../BaseClient';
import type { String$ } from '../compiled-protos/common';
import { Empty } from '../compiled-protos/common';
import type {
  AttachPaymentMethodRequest,
  CancelSubscriptionRequest,
  CreateCustomerRequest,
  CreateSubscriptionRequest,
  DetachPaymentMethodRequest,
  GetCustomerRequest,
  HandleStripeEventRequest,
  ListCustomersRequest,
  ListPaymentMethodsRequest,
  ListSubscriptionsRequest,
  UpdateSubscriptionRequest,
} from '../compiled-protos/stripe';
import {
  ListCustomersResponse,
  ListPaymentMethodsResponse,
  ListSubscriptionsResponse,
  PaymentMethod,
  StripeCustomer,
  StripeEventData,
  StripeEventDataList,
  StripeInvoice,
  Subscription,
} from '../compiled-protos/stripe';
import { StripeServiceClient } from '../compiled-protos/stripe.client';

class StripeClient extends BaseClient {
  self: StripeServiceClient;

  constructor(host: string, userID?: number) {
    super(host, userID);
    this.self = new StripeServiceClient(this.transport);
  }

  // Customer operations
  public async CreateCustomer(req: CreateCustomerRequest) {
    let res = StripeCustomer.create();
    try {
      res = await this.self.createCustomer(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async GetCustomer(req: GetCustomerRequest) {
    let res = StripeCustomer.create();
    try {
      res = await this.self.getCustomer(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async UpdateCustomer(req: StripeCustomer) {
    let res = StripeCustomer.create();
    try {
      res = await this.self.updateCustomer(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async ListCustomers(req: ListCustomersRequest) {
    let res = ListCustomersResponse.create();
    try {
      res = await this.self.listCustomers(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Payment method operations
  public async AttachPaymentMethod(req: AttachPaymentMethodRequest) {
    let res = PaymentMethod.create();
    try {
      res = await this.self.attachPaymentMethod(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async DetachPaymentMethod(req: DetachPaymentMethodRequest) {
    let res = Empty.create();
    try {
      res = await this.self.detachPaymentMethod(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async ListPaymentMethods(req: ListPaymentMethodsRequest) {
    let res = ListPaymentMethodsResponse.create();
    try {
      res = await this.self.listPaymentMethods(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Subscription operations
  public async CreateSubscription(req: CreateSubscriptionRequest) {
    let res = Subscription.create();
    try {
      res = await this.self.createSubscription(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async GetSubscription(req: String$) {
    let res = Subscription.create();
    try {
      res = await this.self.getSubscription(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async UpdateSubscription(req: UpdateSubscriptionRequest) {
    let res = Subscription.create();
    try {
      res = await this.self.updateSubscription(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async CancelSubscription(req: CancelSubscriptionRequest) {
    let res = Subscription.create();
    try {
      res = await this.self.cancelSubscription(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async ListSubscriptions(req: ListSubscriptionsRequest) {
    let res = ListSubscriptionsResponse.create();
    try {
      res = await this.self.listSubscriptions(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Invoice operations
  public async GetInvoice(req: String$) {
    let res = StripeInvoice.create();
    try {
      res = await this.self.getInvoice(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async ListInvoices(req: ListSubscriptionsRequest) {
    let res = ListSubscriptionsResponse.create();
    try {
      res = await this.self.listInvoices(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Webhook handler
  public async HandleStripeEvent(req: HandleStripeEventRequest) {
    let res = Empty.create();
    try {
      res = await this.self.handleStripeEvent(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // StripeEventData CRUD operations
  public async Create(req: StripeEventData) {
    let res = StripeEventData.create();
    try {
      res = await this.self.createStripeEventData(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async Get(req: StripeEventData) {
    let res = StripeEventData.create();
    try {
      res = await this.self.getStripeEventData(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGet(req: StripeEventData) {
    let res = StripeEventDataList.create();
    try {
      res = await this.self.batchGetStripeEventData(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async Update(req: StripeEventData) {
    let res = StripeEventData.create();
    try {
      res = await this.self.updateStripeEventData(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }
}

export { StripeClient, StripeEventData, StripeEventDataList };

